import React from 'react';
import { plata, peso, fecha } from '../utils/formato';
import { enPesos } from '../utils/letras';
import Membrete from './Membrete';
import { useConsecutivo, llaveDeRegistro } from '../utils/consecutivo';

// La remisión de venta: el papel que se va con el queso.
//
// Era el hueco. Comprar leche generaba dos papeles —la relación y el
// comprobante— y vender no generaba ninguno: solo un aviso en pantalla que se
// cierra de un clic. El mayorista se llevaba el queso y quedaba la palabra de
// cada uno sobre cuántos kilos iban y a cuánto.
//
// Lleva el LOTE de cada línea. Es lo único que permite, el día que un queso
// salga malo, saber de qué cuajada era y a quién más se le vendió de ese mismo
// lote. Sin lote impreso, esa pregunta no tiene respuesta.

function delNegocio() {
  try {
    const s = JSON.parse(localStorage.getItem('quesera-sesion'));
    const u = s && s.user;
    return { negocio: u?.negocio || null, nit: u?.nit || null,
             direccion: u?.direccion || null, ciudad: u?.ciudad || null,
             telefono: u?.telefono || null, logo: u?.logo || null };
  } catch { return {}; }
}

function Copia({ v, cual }) {
  const fiada = v.forma_pago === 'credito';
  // Lo que suman los renglones, contado aquí y no traído de afuera: es lo único
  // que hace que la columna de la derecha se pueda comprobar con el dedo.
  const subtotal = (v.lineas || []).reduce((s, l) => s + (l.kilos || 0) * (l.precio || 0), 0);
  const descuento = Math.round(Number(v.descuento) || 0);
  return (
    <div className="comprobante">
      <Membrete v={v} titulo="REMISIÓN DE VENTA" cual={cual} />

      <table className="cuerpo-c" style={{ marginBottom: 14 }}>
        <tbody>
          <tr>
            <td className="rot">Cliente</td>
            <td className="neg">{v.cliente || 'Mostrador'}</td>
            <td className="rot">Fecha</td>
            <td>{fecha(v.fecha)}</td>
          </tr>
          <tr>
            <td className="rot">C.C. o NIT</td>
            <td>{v.documento || '—'}</td>
            <td className="rot">Despacho</td>
            <td>{[v.ciudad, v.direccion].filter(Boolean).join(' · ') || '—'}</td>
          </tr>
        </tbody>
      </table>

      <table className="relacion">
        <thead>
          <tr>
            <th>Producto</th>
            <th>Lote</th>
            <th className="der">Kilos</th>
            <th className="der">Precio</th>
            <th className="der">Total</th>
          </tr>
        </thead>
        <tbody>
          {(v.lineas || []).map((l, n) => (
            <tr key={n}>
              <td>{l.producto}</td>
              {/* El lote va impreso. El día que un queso salga malo, es lo
                  único que permite saber de qué cuajada era y a quién más se
                  le vendió de ese mismo lote. */}
              <td>{l.lote || '—'}</td>
              <td className="der">{peso(l.kilos, l.unidad || 'kg')}</td>
              <td className="der">{plata(l.precio)}</td>
              <td className="der">{plata(l.kilos * l.precio)}</td>
            </tr>
          ))}
          <tr className="subt">
            {/* Con descuento este renglón es el SUBTOTAL y no el total: si
                dijera «TOTAL» habría dos totales distintos en la misma hoja. */}
            <td colSpan={2}>{descuento > 0 ? 'SUBTOTAL' : 'TOTAL'}</td>
            <td className="der">
              {peso((v.lineas || []).reduce((s, l) => s + (l.kilos || 0), 0), 'kg')}
            </td>
            <td></td>
            <td className="der">{plata(descuento > 0 ? subtotal : v.total)}</td>
          </tr>
        </tbody>
      </table>

      {/* El descuento pactado, escrito.
          Sin este renglón los kilos por su precio suman una cifra y el TOTAL
          dice otra menor, sin nada que lo explique — y quien recibe el papel lo
          lee como un error de cuentas, que es lo peor que puede decir una
          factura. Va con los kilos y el precio a la vista para que la resta se
          pueda comprobar igual que las multiplicaciones de arriba. */}
      {descuento > 0 && (
        <table className="detalle" style={{ marginTop: 12 }}>
          <tbody>
            {/* Sin repetir el subtotal: ya está en el último renglón de la
                tabla de arriba, y dos veces la misma cifra hace dudar de si
                son la misma. */}
            <tr>
              <td>
                {/* «DE LA FACTURA», no «sobre el despacho».

                    El descuento se hace una sola vez por factura y cae en el
                    primer despacho del periodo; los demás viajes de esa semana
                    salen sin él. Diciendo «sobre el despacho», el cliente que
                    compare dos remisiones creería que a la segunda se le
                    olvidó. */}
                Menos descuento acordado
                {v.descuento_kilos > 0
                  ? ` — ${peso(v.descuento_kilos, 'kg')} de esta factura`
                  : ''}
              </td>
              <td className="der">−{plata(descuento)}</td>
            </tr>
            <tr className="neto">
              <td>TOTAL</td>
              <td className="der">{plata(v.total)}</td>
            </tr>
          </tbody>
        </table>
      )}

      <p style={{ fontSize: 12, marginTop: 12 }}>
        <strong>Son: {enPesos(v.total)}</strong>
      </p>

      {/* Fiado o pagado, dicho en el papel. Es la diferencia entre un recibo y
          un documento de cobro, y quien lo recibe tiene que saber cuál tiene
          en la mano. */}
      <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8,
                    border: '1px solid #bbb',
                    background: fiada ? '#FFF6E0' : '#EAF5EE' }}>
        <strong>{fiada ? 'QUEDA DEBIENDO' : 'PAGADO DE CONTADO'}</strong>
        {fiada
          ? ` — ${plata(v.total)}. Este despacho queda pendiente de pago.`
          : ' — no queda saldo por este despacho.'}
      </div>

      <div className="firmas">
        <div>
          <div className="linea" />
          <div className="chico">Despachó</div>
        </div>
        <div>
          <div className="linea" />
          <div className="chico">Recibí conforme — {v.cliente || ''}</div>
          <div className="chico">C.C. {v.documento || ''}</div>
        </div>
      </div>
    </div>
  );
}

function Remision({ v, alCerrar }) {
  // EL CONSECUTIVO, no el id de la base.
  //
  // Lo que salía impreso era el `id` de la venta o del pago: un número
  // compartido con las otras empresas del servidor, por eso las ventas de
  // Gabriel empezaron en 102 y van saltando. Pedirle «la 45» a alguien no
  // servía: esa 45 podía no existir en su negocio.
  //
  // Va aquí y no en cada pantalla que abre este papel —al vender, al pagar,
  // y al sacarlo del archivo— porque una cadena de cinco sitios se rompe en
  // el que se olvide. Antes del `return null`, por el orden de los
  // enganches de React.
  const consecutivo = useConsecutivo(v && v.serie,
                                     llaveDeRegistro(v && v.numero),
                                     v && v.fecha);
  if (!v) return null;
  // Sin número el papel sale con el id de antes: es preferible a no salir.
  const p = { ...v, numero: consecutivo || v.numero };
  const datos = { ...delNegocio(), ...p };
  return (
    <div className="capa-imprimir">
      <div className="barra-imprimir">
        <button className="boton suave" onClick={alCerrar}>Cerrar</button>
        <button className="boton" onClick={() => window.print()}>🖨️ Imprimir</button>
      </div>
      <div className="hoja">
        <Copia v={datos} cual="Original — cliente" />
        <div className="corte" />
        <Copia v={datos} cual="Copia — archivo" />
      </div>
    </div>
  );
}

export default Remision;
